import React, { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useIntlBds } from '../../../../../translations/BdsIntlProvider';
import { Box } from '../../../../Box/Box';
import { DragAndDrop } from '../../../../DragAndDrop';
import { Drawer } from '../../../../Drawer/Drawer';
import { Grid } from '../../../../Grid/Grid';
import { Icon } from '../../../../Icon/Icon';
import { IconButton } from '../../../../IconButton/IconButton';
import { Typography } from '../../../../Typography';
import { dataTableClassnames } from '../../const/classNames';
import { internalColumnType } from '../../const/internalColumnType';
import { getHeaderTitle } from '../../utils/getHeaderTitle';
import { useColumnConfiguration } from '../../utils/useColumnConfiguration';

export const ColumnsConfiguration: React.FC = () => {
  const { intl } = useIntlBds();
  const { setColumnOrder, state, managedColumns, setHiddenColumn } = useColumnConfiguration();
  const [open, setOpen] = useState(false);

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const reorderedColumns = reorder(managedColumns ?? [], result.source.index, result.destination.index);

    setColumnOrder([
      internalColumnType.selectRow,
      internalColumnType.expandRow,
      ...reorderedColumns.map((column) => column.id),
      internalColumnType.menu
    ]);
  };

  return (
    <>
      <IconButton
        dataTest={dataTableClassnames.columnsConfiguration.root}
        color={!state.allVisible ? 'active' : undefined}
        aria-label={intl.DataTable.ColumnsConfiguration.label}
        className={dataTableClassnames.columnsConfiguration.root}
        slotProps={{
          tooltip: {
            title: intl.DataTable.ColumnsConfiguration.label
          }
        }}
        iconName="view_column"
        onClick={() => {
          setOpen(true);
        }}
      />
      <Drawer open={open} anchor="right" onClose={() => setOpen(false)}>
        <Box padding={6} minWidth={400}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid container justifyContent="space-between" alignItems="center" gap={4}>
              <Icon name="view_column" />
              <Typography.Heading small>{intl.DataTable.ColumnsConfiguration.label}</Typography.Heading>
            </Grid>
            <Grid>
              <IconButton
                iconName="close"
                dataTest="close-columns-configuration-button"
                onClick={() => {
                  setOpen(false);
                }}
              />
            </Grid>
          </Grid>
          <Typography.Body marginBottom={5}>{intl.DataTable.ColumnsConfiguration.subTitle}</Typography.Body>
          <Box>
            <DragAndDrop onDragEnd={onDragEnd} droppableId="droppable-column-config">
              {managedColumns.map((column, index) => {
                return (
                  <DragAndDrop.Item key={column.id} draggableId={column.id} index={index} icon>
                    <Typography.Body>{getHeaderTitle(column)}</Typography.Body>
                    <IconButton
                      dataTest="data-table-toggle-column-visibility"
                      data-test-value={`${column.id}_${!column.columnDef.enableHiding ? 'blocked' : 'unblocked'}`}
                      disabled={!column.columnDef.enableHiding}
                      iconName={column.getIsVisible() ? 'visibility' : 'visibility_off'}
                      onClick={() => {
                        setHiddenColumn(column);
                      }}
                    />
                  </DragAndDrop.Item>
                );
              })}
            </DragAndDrop>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  if (removed) {
    result.splice(endIndex, 0, removed);
  }

  return result;
}
